import React, { useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";
import { RequiredItem } from "@transfr-inc/dashboard-components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Input,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";
import { OrgFormToggle } from "@components/forms/org-editor";
import "./menu-template.scss";
import { useApiRequest } from "../../utils/http-client";
import { services, toggles } from "../../dependencies";

export const ProgramsForm = ({
  defaultValues,
  register,
  control,
  formState,
}) => {
  const { programsService } = services;
  const { isProfessionalLearningEnabled } = toggles;

  if (!register && !formState && !control) {
    ({ register, formState, control } = useFormContext());
  } else if (!register || !formState || !control) {
    throw (
      "ProgramsForm: for register, control, and formState, all values must be specified or entirely left unspecified " +
      "Specifying a value for only the errors prop, for example, will result in this error message."
    );
  }

  const { errors } = formState;

  const {
    response: allCredentials,
    sendRequest: getAllCredentials,
    loading: allCredentialsLoading,
  } = useApiRequest(() => programsService.getAllCredentials(), false);

  useEffect(() => {
    getAllCredentials();
  }, []);

  const renderProgramNameInput = () => {
    return (
      <Controller
        control={control}
        name="title"
        defaultValue={defaultValues?.title ?? ""}
        rules={{
          required: {
            value: true,
            message: "Required fields are missing.",
          },
          maxLength: 50,
          pattern: {
            value: /^[\d ',.a-z-]+$/i,
            message:
              "Name formatted incorrectly. Only letters, hyphens, numbers, and spaces are allowed.",
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          return (
            <div className="menu-template-field">
              <label>
                Program Name <sup>*</sup>
              </label>
              <Input
                label={
                  <RequiredItem
                    className="menu-template-input-label"
                    text="Name"
                  />
                }
                type="text"
                name="title"
                errors={errors?.title}
                ref={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value ?? ""}
                defaultValue={defaultValues?.title}
              />
            </div>
          );
        }}
      />
    );
  };

  const renderDescriptionTextarea = () => {
    return (
      <Controller
        control={control}
        name="description"
        defaultValue={defaultValues?.description || ""}
        rules={{
          required: {
            value: false,
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          return (
            <div className="program-template-field">
              <Textarea
                label="Program Description"
                name="description"
                ref={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value ?? ""}
                defaultValue={defaultValues?.description}
              />
            </div>
          );
        }}
      />
    );
  };

  const renderAwardsTextarea = () => {
    return (
      <Controller
        control={control}
        name="awardIds"
        defaultValue={defaultValues?.awardIds || ""}
        rules={{
          required: {
            value: false,
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          return (
            <div className="program-template-field">
              <h2 className="section-title">Awards</h2>
              <div className="awards-section">
                <Dropdown
                  className={"awards-dropdown"}
                  selected={value}
                  onOptionSelected={onChange}
                >
                  <DropdownButton
                    placeholder={"Select a credential..."}
                    clearEnabled
                  >
                    {value?.name}
                  </DropdownButton>
                  <DropdownItems>
                    {allCredentials?.map((award) => (
                      <DropdownItem
                        key={award.id}
                        value={award}
                        disabled={award.disabled}
                      >
                        {award.name}
                      </DropdownItem>
                    ))}
                  </DropdownItems>
                </Dropdown>
              </div>
            </div>
          );
        }}
      />
    );
  };

  return (
    <div className="menu-template-form">
      {renderProgramNameInput()}
      {!allCredentialsLoading && renderAwardsTextarea()}
      {renderDescriptionTextarea()}
      {isProfessionalLearningEnabled && (
        <OrgFormToggle
          name="professionalLearning"
          title="Basics"
          infoText="Is this a Basics program?"
          defaultValue={defaultValues?.professionalLearning ?? false}
        />
      )}
    </div>
  );
};
